import {
    DATE_FORMAT,
    getRegistrationEndpoint,
    isValidCif,
    isValidNif,
    RegistrationData,
    UserRegistrationError,
} from 'cpes-common';
import { formatCpDate, trimAllValues } from '@cp-shared-19/common-utilities';
import { parseErrorResponse } from '@cp-shared-19/frontend-integration';
import {
    CleaveInput,
    FocusFirstError,
    preventSubmit,
    Spinner,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    ValidatedCheckbox,
    ValidatedInput,
} from '@cp-shared-19/frontend-ui';
import { Button, ErrorMessage, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { CpDataApi } from 'cp-xhr';
import { Formik } from 'formik';
import React, { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { RegistrationFormProps } from '../RegistrationFormProps';
import { PrivacyPolicyComponent } from '../ui/PrivacyPolicy';
import { maxIbanLength } from '../validations/validationSettings';
import { isEmpty } from 'lodash';
import { useTrackerOnBeforeUnload } from 'hooks';
import { currentBrand } from 'config';

export const BusinessRegistrationForm: React.FC<RegistrationFormProps> = ({
    showErrorCallback,
    successCallback,
    privacyPolicyContent,
}) => {
    const { t } = useTranslation('registration');
    const [showSpinner, setShowSpinner] = useState(false);
    const [showPrivacyPolicy, togglePrivacyPolicy] = useState(false);
    const {
        onTyping,
        onSubmit: trackOnSubmit,
        onError: trackOnError,
        onSuccess: trackOnSuccess,
    } = useAnalyticsFormTracker({
        startTyping: 'onStartTypingConfirmIdentity',
        confirm: 'onConfirmConfirmIdentiy',
        confirmError: 'onConfirmIdentiyAuthFailed',
        confirmSuccess: 'onConfirmIdentiyAuthSuccess',
    });

    const { onAction } = useAnalyticsActionTracker('onFormValidationErrorConfirmIdentity');
    const getErrors = (errors: { [k: string]: string | undefined }): string => Object.keys(errors).join(`, `);
    const initialListErrors = 'iban, constitutionDate, cif, acceptPrivacyPolicy';
    const setLastTouchedField = useTrackerOnBeforeUnload();

    const validationSchema = (): Yup.SchemaOf<object> => {
        return Yup.object().shape({
            iban: Yup.string()
                .required(t('error.too-short-bank-account'))
                .matches(RegExp('^[0-9]*$'), t('error.too-short-bank-account'))
                .test('length', t('error.too-short-bank-account'), iban => !!iban && iban.length === maxIbanLength),
            constitutionDate: Yup.string()
                .required(t('error.constitution-date.required'))
                .test('format', t('error.constitution-date.format'), date => formatCpDate(date, DATE_FORMAT).isValid())
                .test('no future date', t('error.constitution-date.no-future'), date =>
                    formatCpDate(date, DATE_FORMAT)
                        .toMoment()
                        .isSameOrBefore(formatCpDate().toMoment()),
                )
                .test('too old', t('error.constitution-date.too-old'), date =>
                    formatCpDate(date, DATE_FORMAT)
                        .toMoment()
                        .isSameOrAfter(
                            formatCpDate(
                                formatCpDate()
                                    .subtract(300, 'years')
                                    .format(DATE_FORMAT),
                                DATE_FORMAT,
                            ).toMoment(),
                        ),
                ),
            cif: Yup.string()
                .trim()
                .required(t('error.cif.required'))
                .test('is valid nif', t('error.cif.is-valid-nif'), input => !!input && !isValidNif(input))
                .test('cif format', t('error.cif.format'), input => !!input && isValidCif(input)),
            acceptPrivacyPolicy: Yup.bool().oneOf([true], t('error.accept-privacy-policy-required')),
        });
    };

    const labels = {
        iban: {
            text: t('bank-account.label'),
            addon: t('bank-account.addon'),
        },
        constitutionDate: {
            text: t('constitution-date.label'),
        },
        cif: {
            text: t('cif.label'),
        },
        acceptPrivacyPolicy: {
            text: t('accept-privacy-policy'),
            link: t('accept-privacy-policy-link'),
        },
    };

    return (
        <Formik
            initialValues={{
                iban: '',
                constitutionDate: '',
                cif: '',
                passport: '',
                acceptPrivacyPolicy: false,
            }}
            validationSchema={validationSchema()}
            onSubmit={(values): void => {
                setShowSpinner(true);
                const requestBody: RegistrationData = {
                    personalId: values.cif,
                    ibanDigits: values.iban,
                    identificationDate: formatCpDate(values.constitutionDate, DATE_FORMAT).toCpDate(),
                };
                CpDataApi.post(getRegistrationEndpoint(), trimAllValues(requestBody))
                    .then(() => {
                        trackOnSuccess();
                        successCallback();
                    })
                    .catch(error => {
                        trackOnSubmit();
                        trackOnError();
                        const parsedErrorCode = parseErrorResponse<UserRegistrationError>(error).code;
                        const responseErrorCode = (error?.code ||
                            error?.response.code ||
                            error?.response?.data?.code) as UserRegistrationError | undefined;
                        showErrorCallback(responseErrorCode ?? parsedErrorCode);
                    })
                    .finally(() => setShowSpinner(false));
            }}
            data-testid={'businessRegistrationForm'}
        >
            {(formik): JSX.Element => (
                <Form
                    onSubmit={e => preventSubmit(e)}
                    onChange={(): void => {
                        if (!formik.values.constitutionDate) {
                            onTyping(formik.errors, formik.touched);
                        }
                    }}
                >
                    {showSpinner && <Spinner fullPage />}
                    <FocusFirstError />

                    <Fieldset>
                        <Fieldset.Row>
                            <Layout>
                                <Layout.Item default={'1/1'}>
                                    <CleaveInput
                                        cleaveOptions={{
                                            numericOnly: true,
                                        }}
                                        maxLength={maxIbanLength}
                                        className={'iban-input c-form-field--no-floating'}
                                        label={labels.iban.text}
                                        name={'iban'}
                                        addonText={labels.iban.addon}
                                        reversed={true}
                                        inputMode={'numeric'}
                                        testId={'iban'}
                                        onFocus={(): void => setLastTouchedField(labels.iban.text)}
                                    />
                                </Layout.Item>
                            </Layout>
                        </Fieldset.Row>

                        <Fieldset.Row>
                            <Layout>
                                <Layout.Item default={'1/2'} m={'1/1'}>
                                    <CleaveInput
                                        cleaveOptions={{
                                            delimiter: '/',
                                            blocks: [2, 2, 4],
                                            numericOnly: true,
                                        }}
                                        inputMode={'numeric'}
                                        className={'constitution-date-input c-form-field--no-floating'}
                                        label={labels.constitutionDate.text}
                                        name={'constitutionDate'}
                                        handleChange={() => {
                                            onTyping(formik.errors, formik.touched);
                                        }}
                                        onFocus={(): void => setLastTouchedField(labels.constitutionDate.text)}
                                    />
                                </Layout.Item>

                                <Layout.Item default={'1/2'} m={'1/1'}>
                                    <ValidatedInput
                                        className="c-form-field--no-floating"
                                        label={
                                            ((
                                                <>
                                                    {labels.cif.text}
                                                    {currentBrand === 'audi' && <div>&nbsp;</div>}
                                                </>
                                            ) as unknown) as string
                                        }
                                        name={'cif'}
                                        testId={'cif'}
                                        id={'cif'}
                                        onFocus={(): void => setLastTouchedField(labels.cif.text)}
                                    />
                                </Layout.Item>
                            </Layout>
                        </Fieldset.Row>

                        <Fieldset.Row>
                            <Layout>
                                <Layout.Item default={'1/1'}>
                                    <ValidatedCheckbox
                                        name={'acceptPrivacyPolicy'}
                                        testId={'acceptPrivacyPolicy'}
                                        label={
                                            <>
                                                {labels.acceptPrivacyPolicy.text}{' '}
                                                <Button
                                                    onClick={(e: MouseEvent): void => {
                                                        e.preventDefault();
                                                        togglePrivacyPolicy(!showPrivacyPolicy);
                                                    }}
                                                    link={true}
                                                    type="button"
                                                >
                                                    {labels.acceptPrivacyPolicy.link}
                                                </Button>
                                            </>
                                        }
                                        onFocus={(): void =>
                                            setLastTouchedField(
                                                `${labels.acceptPrivacyPolicy.text} ${labels.acceptPrivacyPolicy.link}`,
                                            )
                                        }
                                    />
                                </Layout.Item>
                            </Layout>
                        </Fieldset.Row>

                        {showPrivacyPolicy && <PrivacyPolicyComponent privacyPolicyContent={privacyPolicyContent} />}
                        <Layout>
                            <Layout.Item default={'1/1'}>
                                <Button
                                    full={true}
                                    onClick={async (): Promise<void> => {
                                        await formik.submitForm();
                                        if (!isEmpty(formik.errors) || isEmpty(formik.touched)) {
                                            const errorToString = getErrors(formik.errors).toString();
                                            if (!errorToString) {
                                                onAction(initialListErrors);
                                            } else onAction(getErrors(formik.errors));
                                        }
                                    }}
                                    testId={'submitButton'}
                                    type="submit"
                                >
                                    {t('translation:editableSectionNav.confirm')}
                                </Button>
                                {!formik.isValid && !formik.isSubmitting && formik.submitCount > 0 && (
                                    <ErrorMessage>{t('error.not-all-steps-done')}</ErrorMessage>
                                )}
                            </Layout.Item>
                        </Layout>
                    </Fieldset>
                </Form>
            )}
        </Formik>
    );
};
